import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { departmentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const EMPTY_FORM = { name: '', parent_id: '', cost_center: '' };

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create, obj = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const canEdit = ['admin', 'hr_manager'].includes(user?.role);
  const canDelete = user?.role === 'admin';

  const load = () => {
    setLoading(true);
    departmentAPI.list()
      .then(r => setDepartments(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (dept) => {
    setEditTarget(dept);
    setForm({ name: dept.name, parent_id: dept.parent_id || '', cost_center: dept.cost_center || '' });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setFormError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        parent_id: form.parent_id || null,
        cost_center: form.cost_center || null,
      };
      if (editTarget) {
        await departmentAPI.update(editTarget.id, payload);
      } else {
        await departmentAPI.create(payload);
      }
      closeForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Lỗi lưu phòng ban');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (dept) => {
    setDeleteError('');
    if (!window.confirm(`Xóa phòng ban "${dept.name}"?`)) return;
    try {
      await departmentAPI.delete(dept.id);
      load();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Lỗi xóa phòng ban');
    }
  };

  // Sắp xếp: phòng gốc trước, phòng con thụt vào
  const renderTree = () => {
    const roots = departments.filter(d => !d.parent_id);
    const children = (parentId) => departments.filter(d => d.parent_id === parentId);
    const rows = [];
    const addRow = (dept, depth) => {
      rows.push({ ...dept, depth });
      children(dept.id).forEach(child => addRow(child, depth + 1));
    };
    roots.forEach(r => addRow(r, 0));
    return rows;
  };

  const treeRows = renderTree();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>🏬 Phòng ban</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
            Quản lý cơ cấu tổ chức và phòng ban
          </p>
        </div>
        {canEdit && (
          <button onClick={openCreate} style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg,#1e3a8a,#0891b2)',
            color: '#fff', border: 'none', borderRadius: 8,
            cursor: 'pointer', fontWeight: 600, fontSize: 14,
          }}>
            + Thêm phòng ban
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Tổng phòng ban', value: departments.length, color: '#1e3a8a' },
          { label: 'Phòng ban gốc', value: departments.filter(d => !d.parent_id).length, color: '#0891b2' },
          { label: 'Phòng ban con', value: departments.filter(d => !!d.parent_id).length, color: '#7c3aed' },
          { label: 'Tổng nhân viên', value: departments.reduce((s, d) => s + (parseInt(d.employee_count) || 0), 0), color: '#16a34a' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            flex: 1, background: '#fff', borderRadius: 10, padding: '16px 20px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`,
          }}>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Delete error banner */}
      {deleteError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          padding: '10px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>⚠️ {deleteError}</span>
          <button onClick={() => setDeleteError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Tên phòng ban', 'Phòng ban cha', 'Mã trung tâm chi phí', 'Nhân viên đang làm', canEdit ? 'Hành động' : ''].filter(Boolean).map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>⏳ Đang tải...</td></tr>
            ) : treeRows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Chưa có phòng ban nào</td></tr>
            ) : treeRows.map(dept => (
              <tr key={dept.id}
                style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                onClick={() => navigate(`/departments/${dept.id}/${slugify(dept.name)}`)}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: dept.depth * 24 }}>
                    {dept.depth > 0 && (
                      <span style={{ color: '#cbd5e1', fontSize: 16, flexShrink: 0 }}>└</span>
                    )}
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: dept.depth === 0
                        ? 'linear-gradient(135deg,#1e3a8a,#0891b2)'
                        : 'linear-gradient(135deg,#7c3aed,#0891b2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 14,
                    }}>
                      {dept.depth === 0 ? '🏬' : '📂'}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1d4ed8' }}>{dept.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {dept.depth === 0 ? 'Phòng ban chính · Nhấn để xem chi tiết →' : 'Nhấn để xem chi tiết →'}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', color: '#64748b' }}>
                  {dept.parent_name ? (
                    <span style={{ background: '#f1f5f9', padding: '3px 10px', borderRadius: 6, fontSize: 12 }}>{dept.parent_name}</span>
                  ) : (
                    <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {dept.cost_center ? (
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0891b2', background: '#f0f9ff', padding: '2px 8px', borderRadius: 4 }}>{dept.cost_center}</span>
                  ) : (
                    <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: dept.employee_count > 0 ? '#dcfce7' : '#f1f5f9',
                    color: dept.employee_count > 0 ? '#16a34a' : '#94a3b8',
                    padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  }}>
                    👤 {dept.employee_count || 0} NV
                  </span>
                </td>
                {canEdit && (
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={e => { e.stopPropagation(); openEdit(dept); }} style={{
                        padding: '5px 12px', background: '#eff6ff', color: '#1d4ed8',
                        border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                      }}>
                        ✏️ Sửa
                      </button>
                      {canDelete && (
                        <button onClick={e => { e.stopPropagation(); handleDelete(dept); }} style={{
                          padding: '5px 12px', background: '#fef2f2', color: '#dc2626',
                          border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                        }}>
                          🗑️ Xóa
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Create / Edit */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
              {editTarget ? '✏️ Sửa phòng ban' : '🏬 Thêm phòng ban mới'}
            </h3>

            {formError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
                ⚠️ {formError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Tên phòng ban <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text" required
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="VD: Human Resources, Engineering..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Phòng ban cha (nếu là phòng ban con)
                </label>
                <select
                  value={form.parent_id}
                  onChange={e => setForm({ ...form, parent_id: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                >
                  <option value="">— Không có (phòng ban gốc) —</option>
                  {departments
                    .filter(d => !editTarget || d.id !== editTarget.id)
                    .map(d => (
                      <option key={d.id} value={d.id}>
                        {d.parent_id ? `  └ ${d.name}` : d.name}
                      </option>
                    ))}
                </select>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Mã trung tâm chi phí
                </label>
                <input
                  type="text"
                  value={form.cost_center}
                  onChange={e => setForm({ ...form, cost_center: e.target.value })}
                  placeholder="VD: HR-001, IT-002..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeForm}
                  style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff', fontSize: 14 }}>
                  Hủy
                </button>
                <button type="submit" disabled={submitting} style={{
                  padding: '10px 24px',
                  background: submitting ? '#93c5fd' : 'linear-gradient(135deg,#1e3a8a,#0891b2)',
                  color: '#fff', border: 'none', borderRadius: 8,
                  cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
                }}>
                  {submitting ? '⏳ Đang lưu...' : (editTarget ? 'Cập nhật' : 'Tạo phòng ban')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
